/**
 * The catalogue coπ plays from (Gordon 2026-10-31: "play song, genre, bpm,
 * playlist, video, play random release, track, video"). Loaded once per visit
 * on the first "play …" (a few hundred small public docs), then reused for
 * five minutes — lib/copi-media does the picking.
 */

import { db } from '@/lib/firebase';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { getCoverIndex } from '@/services/cover-index';
import type { CopiArtist, CopiLibrary, CopiPlace, CopiPlaylist, CopiRelease, CopiTrack, CopiVideo } from '@/lib/copi-media';

const isPublic = (d: any) =>
  !(d?.privacy === 'private' || d?.privacy === 'unlisted' || d?.status === 'draft' || d?.status === 'unlisted'
    || d?.status === 'archived' || d?.unlisted === true || d?.adminHidden || d?.hidden === true);
const artistOf = (d: any): string =>
  (typeof d?.artist === 'string' ? d.artist : d?.artist?.name) || d?.artistName || '';
const genresOf = (d: any): string[] =>
  (Array.isArray(d?.genres) && d.genres.length ? d.genres : [d?.genre]).filter((g: unknown) => typeof g === 'string' && g.trim());
const audioOf = (d: any): string => String(d?.mp3Url || d?.audioUrl || '').trim();

let cache: { at: number; uid: string; lib: Promise<CopiLibrary> } | null = null;

export function loadCopiLibrary(uid?: string | null): Promise<CopiLibrary> {
  const key = uid || '';
  if (cache && cache.uid === key && Date.now() - cache.at < 5 * 60_000) return cache.lib;
  const lib = build(key);
  lib.catch(() => { cache = null; });
  cache = { at: Date.now(), uid: key, lib };
  return lib;
}

async function build(uid: string): Promise<CopiLibrary> {
  const [works, releases, videos, publicLists, ownLists, covers, artistDocs] = await Promise.all([
    getDocs(query(collection(db, 'works'), limit(3000))),
    getDocs(query(collection(db, 'releases'), limit(1000))),
    getDocs(query(collection(db, 'videos'), limit(300))).catch(() => null),
    // Playlist tracks are readable only when signed in — no playlists for visitors.
    uid ? getDocs(query(collection(db, 'playlists'), where('visibility', '==', 'public'))).catch(() => null) : Promise.resolve(null),
    uid ? getDocs(query(collection(db, 'playlists'), where('creatorId', '==', uid))).catch(() => null) : Promise.resolve(null),
    getCoverIndex().catch(() => null),
    getDocs(query(collection(db, 'artists'), limit(1000))).catch(() => null),
  ]);

  // An artist's genres count for their tracks too — "2 Step" was set only on
  // the artist Sunrise, never on a track (Gordon 2026-10-31: "play some two step").
  const artistGenres = new Map<string, string[]>();
  artistDocs?.docs.forEach(d => {
    const a = d.data() as any;
    if (a.name && !a.adminHidden) artistGenres.set(String(a.name).toLowerCase().trim(), genresOf(a));
  });
  const withArtist = (genres: string[], artist: string) =>
    [...new Set([...genres, ...(artistGenres.get(artist.toLowerCase().trim()) || [])])];

  // Tracks: every public work with audio; a release's genre fills in for a
  // work that has none of its own.
  const tracks = new Map<string, CopiTrack>();
  const releaseOf = new Map<string, any>();
  const rels: CopiRelease[] = [];
  for (const d of releases.docs) {
    const r = d.data() as any;
    if (!isPublic(r) || r.incomplete === true) continue;
    (Array.isArray(r.tracks) ? r.tracks : []).forEach((t: any) => { const id = t?.workId || t?.id; if (id && !releaseOf.has(id)) releaseOf.set(id, { id: d.id, ...r }); });
  }
  for (const d of works.docs) {
    const w = d.data() as any;
    const audioUrl = audioOf(w);
    if (!audioUrl || !isPublic(w) || w.type === 'video') continue;
    const rel = releaseOf.get(d.id);
    const artist = artistOf(w) || artistOf(rel);
    const genres = withArtist(genresOf(w).length ? genresOf(w) : genresOf(rel), artist);
    tracks.set(d.id, {
      id: d.id, title: w.title || 'Track', artist, genres,
      bpm: Number(w.bpm) > 0 ? Number(w.bpm) : undefined, audioUrl,
      imageUrl: covers?.forWork({ id: d.id, ...w }) || rel?.coverArtUrl || '',
      releaseId: rel?.id, explicit: w.explicit === true,
    });
  }
  for (const d of releases.docs) {
    const r = d.data() as any;
    if (!isPublic(r) || r.incomplete === true) continue;
    const cover = r.coverArtUrl || r.imageUrl || (Array.isArray(r.images) && r.images[0]) || '';
    const list: any[] = (Array.isArray(r.tracks) ? r.tracks : []).slice()
      .sort((a: any, b: any) => (a?.trackNumber ?? 0) - (b?.trackNumber ?? 0));
    const trackIds: string[] = [];
    list.forEach((t: any, i: number) => {
      const id = t?.workId || t?.id || `${d.id}-${t?.trackNumber ?? i + 1}`;
      // A track whose audio sits only on the release's own list.
      if (!tracks.has(id) && audioOf(t)) {
        const artist = t.artistName || artistOf(r);
        tracks.set(id, {
          id, title: t.title || r.title || 'Track', artist, genres: withArtist(genresOf(r), artist),
          audioUrl: audioOf(t), imageUrl: cover, releaseId: d.id, explicit: t.explicit === true || r.explicit === true,
        });
      }
      if (tracks.has(id)) trackIds.push(id);
    });
    rels.push({ id: d.id, title: r.title || 'Release', artist: artistOf(r), genres: genresOf(r), imageUrl: cover, trackIds, explicit: r.explicit === true });
  }

  const vids: CopiVideo[] = (videos?.docs || [])
    .map(d => ({ id: d.id, ...(d.data() as any) }))
    .filter(v => v.youtubeId && isPublic(v))
    .map(v => ({
      id: v.id, title: v.title || 'Video', artist: artistOf(v), youtubeId: v.youtubeId, genres: genresOf(v),
      imageUrl: v.thumbnailUrl || `https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`,
    }));

  const lists = new Map<string, CopiPlaylist>();
  for (const snap of [publicLists, ownLists]) {
    snap?.docs.forEach(d => {
      const p = d.data() as any;
      lists.set(d.id, { id: d.id, title: p.title || 'Playlist', owner: p.creatorName || '', trackCount: typeof p.trackCount === 'number' ? p.trackCount : undefined });
    });
  }

  const artists: CopiArtist[] = (artistDocs?.docs || [])
    .map(d => ({ id: d.id, ...(d.data() as any) }))
    .filter(a => a.name && !a.adminHidden && a.hidden !== true)
    .map(a => ({ id: a.id, name: String(a.name), href: `/artists/${a.slug || a.id}` }));

  return { tracks: [...tracks.values()], releases: rels, videos: vids, playlists: [...lists.values()], artists };
}

/** People, labels, shop items and events for "show me <name>" (Gordon
 *  2026-11-02: "show me Ski" opens Ski's profile). Loaded on the first "show
 *  me …" that names something, reused for five minutes. A list visitors can't
 *  read (members) just comes back empty. */
let placesCache: { at: number; list: Promise<CopiPlace[]> } | null = null;
export function loadCopiPlaces(): Promise<CopiPlace[]> {
  if (placesCache && Date.now() - placesCache.at < 5 * 60_000) return placesCache.list;
  const list = buildPlaces();
  list.catch(() => { placesCache = null; });
  placesCache = { at: Date.now(), list };
  return list;
}

async function buildPlaces(): Promise<CopiPlace[]> {
  const [users, labels, items, events] = await Promise.all([
    getDocs(query(collection(db, 'users'), limit(1000))).catch(() => null),
    getDocs(query(collection(db, 'labels'), limit(500))).catch(() => null),
    getDocs(query(collection(db, 'item'), where('status', '==', 'active'), limit(500))).catch(() => null),
    getDocs(query(collection(db, 'events'), limit(300))).catch(() => null),
  ]);
  const out: CopiPlace[] = [];
  users?.docs.forEach(d => {
    const u = d.data() as any;
    if (u.accountType === 'ghost' || u.awaitingClaim || u.hidden || u.adminHidden || u.deleted) return;
    const name = String(u.displayName || '').trim();
    if (name) out.push({ kind: 'person', name, href: `/profile/${d.id}` });
  });
  labels?.docs.forEach(d => {
    const l = d.data() as any;
    if (l.adminHidden || l.hidden) return;
    const name = String(l.name || '').trim();
    if (name) out.push({ kind: 'label', name, href: `/labels/${d.id}` });
  });
  items?.docs.forEach(d => {
    const it = d.data() as any;
    if (it.adminHidden) return;
    const name = String(it.title || '').trim();
    if (name) out.push({ kind: 'item', name, href: `/marketplace/${d.id}` });
  });
  events?.docs.forEach(d => {
    const e = d.data() as any;
    if (e.adminHidden || e.hidden) return;
    const name = String(e.title || e.name || '').trim();
    if (name) out.push({ kind: 'event', name, href: `/events/${d.id}` });
  });
  return out;
}
